import { WorkbookPostMessageTypes } from 'cadenza/workbook/view/view-content/workbook-post-message-types';
import { logger } from 'cadenza/utils/logging';

/**
 * Post-message communication for view notifications and change handling
 * on the iframe side.
 */
export class NotificationIframePostMessageApi {

  /** @type {import('cadenza/workbook/view-content/workbook-iframe-post-message-api').WorkbookIframePostMessageApi} */
  _workbookIFramePostMessageApi;

  /**
   * @param {import('cadenza/workbook/view-content/workbook-iframe-post-message-api').WorkbookIframePostMessageApi} workbookIFramePostMessageApi
   * @param {object} options - Additional (optional) options.
   */
  constructor (workbookIFramePostMessageApi, options = {}) {
    this._workbookIFramePostMessageApi = workbookIFramePostMessageApi;
  }

  /**
   * Informs the parent frame about a notifications on the iframe side.
   *
   * @param {import('./view-notification').ViewNotificationChange} notifications - The changed notifications.
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  notificationsChanged (notifications) {
    return this._workbookIFramePostMessageApi.request({
      type: WorkbookPostMessageTypes.NOTIFICATION_CHANGED,
      detail: {
        added: [], // Default
        removed: [], // Default
        ...notifications
      }
    });
  }

  /**
   * Informs the parent frame about new notifications.
   *
   * @param {...ViewNotification} notifications - The new notifications to add
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  addNotification (...notifications) {
    return this.notificationsChanged({ added: notifications });
  }

  /**
   * Informs the parent frame about obsolete notifications.
   *
   * @param {...ViewNotification} notifications - The new notifications to add
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  removeNotification (...notifications) {
    return this.notificationsChanged({ removed: notifications });
  }

  /**
   * Indicates a change in the view (other than command execution), which should mark the
   * workbook as changed.
   */
  notifyParentOfWorkbookChange () {
    this._workbookIFramePostMessageApi.request({ type: WorkbookPostMessageTypes.CHANGED })
      .catch(error => logger.error('Failed to notify the workbook about a view change', error));
  }

}
